import React, {memo, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {CameraBrandModel, CameraModel} from '../../../../types/control';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useQuestion} from '../../../../hooks/question';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {useMapSelector} from '../../../../hooks/map-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {isValidIP, isValidPort, isValidString, isValidURL} from '../../../../utils/validation';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {AreaModel} from '../../../../store/control/state';
import {useMap} from '../../../../hooks/map';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';

const BACK_URL = '/settings/camera';

// const CAMERA_PROTOCOL_LIST = {
//     'http': 'HTTP',
// };

// const CAMERA_PROTOCOL_SELECT_ITEM_LIST: SelectItem<string>[] =
//     Object.entries(CAMERA_PROTOCOL_LIST).map(([id, name]) => ({value: id, label: name}));

export const CameraEditScreen = memo(function CameraEditScreen() {
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const cameraList = useSelector(state => state.control.cameraList);
    const cameraBrandOptionList: SelectItem<CameraBrandModel /*| 'manual'*/>[] = useMapSelector(
        state => state.control.cameraBrandList,
        cameraBrand => ({
            value: cameraBrand,
            label: cameraBrand.name,
        }),
        // {
        //     value: 'manual',
        //     label: 'Manual',
        // },
    );
    const areaOptionList = useMap(
        useFilterSelector(state => state.control.areaList, area => area.type === 'place', []),
        area => ({value: area, label: area.name}),
        []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => cameraList.find(device => device.id === params['id']) || null, []);
    const [name, setName] = useState(selected?.name || '');
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(
        selected == null ? areaOptionList[0] || null :
            areaOptionList.find(areaOption => areaOption.value.id === selected.area_id) || null);
    const [cameraBrandModel, setCameraBrandModel] = useState<SelectItem<CameraBrandModel /*| 'manual'*/>>(
        selected == null ? cameraBrandOptionList[0] || null :
            cameraBrandOptionList.find(cameraBrandOption => cameraBrandOption.value.id === selected.camera_brand_id)
            || null,
    );

    const [ip, setIP] = useState(selected?.ip || '');

    const [username, setUsername] = useState(selected?.username || '');
    const [password, setPassword] = useState(selected?.password || '');

    const [useRTSP, setUseRTSP] = useState(Boolean(selected?.rtsp?.port));
    const [rtspPort, setRTSPPort] = useState(selected?.rtsp?.port?.toString() || '');
    const [rtspRemotePort, setRTSPRemotePort] = useState(selected?.rtsp?.remote_port?.toString() || '');

    const [useVideoCGI, setUseVideoCGI] = useState(Boolean(selected?.video_cgi?.port));
    const [videoCGIPort, setVideoCGIPort] = useState(selected?.video_cgi?.port?.toString() || '');
    const [videoCGIRemotePort, setVideoCGIRemotePort] = useState(selected?.video_cgi?.remote_port?.toString() || '');


    const [useMJPEG, setUseMJPEG] = useState(Boolean(selected?.mjpeg?.port));
    const [mjpegPort, setMJPEGPort] = useState(selected?.mjpeg?.port?.toString() || '');
    const [mjpegRemotePort, setMJPEGRemotePort] = useState(selected?.mjpeg?.remote_port?.toString() || '');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen geçerli bir kamera adı giriniz.');

            return false;
        }

        if (areaOption?.value == null) {
            failure('Lütfen mahal (alan) seçiniz.');

            return false;
        }

        if (cameraBrandModel == null || cameraBrandModel.value == null) {
            failure('Lütfen geçerli bir marka/model seçiniz.');

            return false;
        }

        if(!(useMJPEG || useVideoCGI || useRTSP)){
            failure('Lütfen en az bir protokol seçiniz.');

            return false;
        }

        if (!isValidIP(ip)) {
            failure('Lütfen geçerli bir IP adresi giriniz.');

            return false;
        }

        if (cameraBrandModel.value.authentication_required) {
            if (!isValidString(username)) {
                failure('Lütfen geçerli bir kullanıcı adı giriniz.');

                return false;
            }

            if (!isValidString(password)) {
                failure('Lütfen geçerli bir şifre giriniz.');

                return false;
            }
        }

        if (cameraBrandModel.value.rtsp && useRTSP) {
            if (!isValidPort(rtspPort)) {
                failure('Lütfen geçerli bir RTSP portu giriniz.');

                return false;
            }

            if (!isValidPort(rtspRemotePort)) {
                failure('Lütfen geçerli bir RTSP Remote portu giriniz.');

                return false;
            }
        }

        if (cameraBrandModel.value.mjpeg && useMJPEG) {
            if (!isValidPort(mjpegPort)) {
                failure('Lütfen geçerli bir MJPEG portu giriniz.');

                return false;
            }

            if (!isValidPort(mjpegRemotePort)) {
                failure('Lütfen geçerli bir MJPEG Remote portu giriniz.');

                return false;
            }
        }

        if (cameraBrandModel.value.video_cgi && useVideoCGI) {
            if (!isValidPort(videoCGIPort)) {
                failure('Lütfen geçerli bir VideoCGI portu giriniz.');

                return false;
            }

            if (!isValidPort(videoCGIRemotePort)) {
                failure('Lütfen geçerli bir VideoCGI Remote portu giriniz.');

                return false;
            }
        }
        return true;
    }, [
        areaOption?.value,
        cameraBrandModel,
        failure,
        ip,
        mjpegPort,
        mjpegRemotePort,
        name,
        password,
        rtspPort,
        rtspRemotePort,
        useMJPEG,
        useRTSP,
        useVideoCGI,
        username,
        videoCGIPort,
        videoCGIRemotePort,
    ]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const cameraModel: CameraModel = {
            id: selected != null ? selected.id : null,
            name,
            camera_brand_id: cameraBrandModel.value.id,
            area_id: areaOption.value.id,
            username: cameraBrandModel.value.authentication_required ? username : '',
            password: cameraBrandModel.value.authentication_required ? password : '',
            ip,
        };

        if (cameraBrandModel.value.rtsp && useRTSP) {
            cameraModel.rtsp = {
                port: Number(rtspPort),
                remote_port: Number(rtspRemotePort),
            };
        }

        if (cameraBrandModel.value.mjpeg && useMJPEG) {
            cameraModel.mjpeg = {
                port: Number(mjpegPort),
                remote_port: Number(mjpegRemotePort),
            };
        }

        if (cameraBrandModel.value.video_cgi && useVideoCGI) {
            cameraModel.video_cgi = {
                port: Number(videoCGIPort),
                remote_port: Number(videoCGIRemotePort),
            };
        }

        return rpc.updateCamera(cameraModel)
            .then(() => {
                success('Cihaz başarılı bir şekilde ' + (cameraModel.id ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            }).catch(failure);
    }, [
        isFieldsValid,
        selected,
        name,
        cameraBrandModel?.value?.id,
        cameraBrandModel?.value?.authentication_required,
        cameraBrandModel?.value?.rtsp,
        cameraBrandModel?.value?.mjpeg,
        cameraBrandModel?.value?.video_cgi,
        areaOption?.value?.id,
        username,
        password,
        ip,
        useRTSP,
        useMJPEG,
        useVideoCGI,
        rpc,
        failure,
        rtspPort,
        rtspRemotePort,
        mjpegPort,
        mjpegRemotePort,
        videoCGIPort,
        videoCGIRemotePort,
        success,
        onBackPressed,
    ]);

    const onDeletePressed = useCallback(() => {
        question('Silmek istediğinizden emin misiniz?', () => {
            return rpc.deleteCamera(selected.id)
                .then(() => {
                    success('Kamera başarılı bir şekilde silindi.');
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [failure, onBackPressed, question, rpc, selected, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadInput, setActiveNumpadExtendedInput]);

    const buttons = useMemo(() =>
        [
            {
                text: 'SİL',
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: 'KAYDET',
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: 'İPTAL',
                className: 'red',
                url: BACK_URL,
            },
        ], [selected, onSavePressed, onDeletePressed],
    );
console.log({cameraBrandModel});
    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Kamera adı yazınız...'
                            label='Kamera Adı'
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label='Mahal (Alan)'
                            options={areaOptionList}
                            onChange={setAreaOption}
                            value={areaOption}/>
                        <SettingSelectComponent
                            label='Marka / Model'
                            options={cameraBrandOptionList}
                            onChange={setCameraBrandModel}
                            value={cameraBrandModel}/>
                        <SettingInputComponent
                            placeholder='IP yazınız...'
                            label='IP'
                            value={ip}
                            setValue={setIP}
                            setActiveInput={setActiveNumpadExtendedInput}
                            onBlur={onBlur}/>
                        {
                            renderIf(cameraBrandModel?.value?.authentication_required)(() => (
                                <>
                                    <SettingInputComponent
                                        placeholder='Kullanıcı adı yazınız...'
                                        label='Kullanıcı Adı'
                                        value={username}
                                        setValue={setUsername}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        type='password'
                                        placeholder='Şifre yazınız...'
                                        label='Şifre'
                                        value={password}
                                        setValue={setPassword}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                </>
                            ))
                        }
                    </div>
                    <div className='content-wrapper'>
                        {
                            renderIf(cameraBrandModel?.value?.rtsp)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='RTSP'
                                        set={() => setUseRTSP(r => !r)}
                                        value={useRTSP}/>
                                    {
                                        renderIf(useRTSP)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder='Port yazınız...'
                                                    label='Port'
                                                    value={rtspPort}
                                                    setValue={setRTSPPort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='Remote Port yazınız...'
                                                    label='Remote Port'
                                                    value={rtspRemotePort}
                                                    setValue={setRTSPRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                        {
                            renderIf(cameraBrandModel?.value?.video_cgi)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='VideoCGI'
                                        set={() => setUseVideoCGI(r => !r)}
                                        value={useVideoCGI}/>
                                    {
                                        renderIf(useVideoCGI)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder='Port yazınız...'
                                                    label='Port'
                                                    value={videoCGIPort}
                                                    setValue={setVideoCGIPort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='Remote Port yazınız...'
                                                    label='Remote Port'
                                                    value={videoCGIRemotePort}
                                                    setValue={setVideoCGIRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                        {
                            renderIf(cameraBrandModel?.value?.mjpeg)(() => (
                                <>
                                    <SettingCheckBoxComponent
                                        label='MJPEG'
                                        set={() => setUseMJPEG(r => !r)}
                                        value={useMJPEG}/>
                                    {
                                        renderIf(useMJPEG)(() => (
                                            <>
                                                <SettingInputComponent
                                                    placeholder='Port yazınız...'
                                                    label='Port'
                                                    value={mjpegPort}
                                                    setValue={setMJPEGPort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder='Remote Port yazınız...'
                                                    label='Remote Port'
                                                    value={mjpegRemotePort}
                                                    setValue={setMJPEGRemotePort}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>
                                        ))
                                    }
                                </>
                            ))
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
